import React from 'react';
import { motion } from 'framer-motion';
import { TrendingUp, Cpu, Building2, Zap, CheckCircle, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useLanguage } from '@/contexts/LanguageContext';
import SafeSEO from '@/components/SafeSEO';
import { Link } from 'react-router-dom';
import CtaSection from '@/components/CtaSection';

const Services = () => {
  const { t } = useLanguage();

  const services = [
    {
      icon: TrendingUp,
      title: t('services.items.investment.title'),
      description: t('services.items.investment.desc'),
      features: t('services.items.investment.features'),
      image: 'https://horizons-cdn.hostinger.com/9442a93f-81a4-4857-bcb8-ca6535ca46c4/db804f2c8dc930cbf3db0df5ec805563.png',
    },
    {
      icon: Cpu,
      title: t('services.items.digital.title'),
      description: t('services.items.digital.desc'),
      features: t('services.items.digital.features'),
      image: 'https://horizons-cdn.hostinger.com/9442a93f-81a4-4857-bcb8-ca6535ca46c4/ecbf5458c163e3982a9872a2945d1b98.png',
    },
    {
      icon: Building2,
      title: t('services.items.realEstate.title'),
      description: t('services.items.realEstate.desc'),
      features: t('services.items.realEstate.features'),
      image: 'Modern commercial real estate development with sustainable architecture and innovative design',
    },
    {
      icon: Zap,
      title: t('services.items.energy.title'),
      description: t('services.items.energy.desc'),
      features: t('services.items.energy.features'),
      image: 'Sustainable energy infrastructure with solar panels, wind turbines and modern oil and gas facilities',
    },
  ];

  return (
    <>
      <SafeSEO
        title={t('services.title')}
        description={t('services.subtitle')}
        url="https://consulogic-gt-com-544311.hostingersite.com/servicios"
      />

      <div className="pt-20">
        <section className="relative py-20 bg-gradient-to-br from-[#0A5F7F] to-[#0D7FA3] text-white">
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="max-w-3xl mx-auto text-center"
            >
              <h1 className="text-4xl md:text-5xl font-bold mb-6">{t('services.title')}</h1>
              <p className="text-xl text-white/90 leading-relaxed">
                {t('services.subtitle')}
              </p>
            </motion.div>
          </div>
        </section>

        <section className="py-20 bg-white">
          <div className="container mx-auto px-4">
            <div className="space-y-24">
              {services.map((service, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 50 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-100px" }}
                  transition={{ duration: 0.8, ease: "easeOut" }}
                  className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${index % 2 !== 0 ? 'lg:flex-row-reverse' : ''}`}
                >
                  <div className={`${index % 2 !== 0 ? 'lg:order-2' : ''} p-8 lg:p-12 bg-white/40 backdrop-blur-xl rounded-[2.5rem] border border-white/20 shadow-2xl shadow-gray-200/50`}>
                    <div className="w-20 h-20 bg-gradient-to-br from-[#0A5F7F] to-[#0D7FA3] rounded-[1.5rem] flex items-center justify-center mb-8 shadow-xl shadow-[#0A5F7F]/20">
                      <service.icon className="h-10 w-10 text-white" />
                    </div>
                    <h2 className="text-4xl font-bold text-[#0A5F7F] mb-6 tracking-tight">{service.title}</h2>
                    <p className="text-lg text-gray-600 mb-8 leading-relaxed font-medium">{service.description}</p>
                    <div className="space-y-4">
                      {Array.isArray(service.features) && service.features.map((feature, idx) => (
                        <div key={idx} className="flex items-start space-x-4 group/item">
                          <div className="mt-1 flex-shrink-0 w-6 h-6 rounded-full bg-[#0A5F7F]/10 flex items-center justify-center group-hover/item:bg-[#0A5F7F]/20 transition-colors">
                            <CheckCircle className="h-4 w-4 text-[#0A5F7F]" />
                          </div>
                          <span className="text-gray-700 font-medium">{feature}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className={`${index % 2 !== 0 ? 'lg:order-1' : ''} relative group`}>
                    <div className="absolute -inset-4 bg-gradient-to-r from-[#0A5F7F]/10 to-[#0D7FA3]/10 rounded-[3rem] blur-2xl opacity-0 group-hover:opacity-100 transition-opacity" />
                    <img
                      className="w-full h-[600px] object-cover rounded-[2.5rem] shadow-2xl relative z-10 border border-white/20 transition-transform duration-700 group-hover:scale-[1.02]"
                      alt={service.title}
                      src={service.image.startsWith('http') ? service.image : 'https://images.unsplash.com/photo-1616352595676-409c19b3cb4a'}
                    />
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-20 bg-gradient-to-br from-gray-50 to-white">
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="max-w-3xl mx-auto text-center"
            >
              <h2 className="text-3xl md:text-4xl font-bold text-[#0A5F7F] mb-6">
                {t('services.customTitle')}
              </h2>
              <p className="text-gray-600 mb-8 leading-relaxed">
                {t('services.customSubtitle')}
              </p>
              <Link to="/contacto">
                <Button size="lg" className="bg-[#0A5F7F] text-white hover:bg-[#0A5F7F]/90">
                  {t('home.scheduleBtn')}
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
            </motion.div>
          </div>
        </section>
      </div>
    </>
  );
};

export default Services;